/**
 * Docker's human sizes, read back so a list of them can be totalled.
 *
 * Docker writes a volume's size with go-units' `HumanSize`: powers of 1000, four
 * significant figures, no space — "1.234GB", "796kB", "0B". That shape is read,
 * and the binary one ("1.5GiB") whose unit cannot be mistaken; anything else —
 * "N/A", a figure some other tool wrote — counts as unmeasured, never guessed at.
 */
const UNITS: Record<string, number> = {
  B: 1,
  kB: 1e3,
  KB: 1e3,
  MB: 1e6,
  GB: 1e9,
  TB: 1e12,
  PB: 1e15,
  KiB: 1024,
  MiB: 1024 ** 2,
  GiB: 1024 ** 3,
  TiB: 1024 ** 4,
  PiB: 1024 ** 5,
};

/** Bytes, or null for a size Docker did not measure or wrote in a shape not read here. */
export function parseDockerSize(value: string | null): number | null {
  if (!value) return null;
  const match = /^(\d+(?:\.\d+)?) ?([A-Za-z]+)$/.exec(value.trim());
  const unit = match ? UNITS[match[2]!] : undefined;
  return match && unit !== undefined ? Number(match[1]) * unit : null;
}

/**
 * What the readable sizes add up to, and whether that was all of them. A
 * `partial` total is a floor — "at least" — and says nothing about the rest.
 * Null when not one size could be read, which is no total rather than zero.
 */
export function totalDockerSizes(
  sizes: (string | null)[],
): { bytes: number; partial: boolean } | null {
  const read = sizes.map(parseDockerSize).filter((bytes): bytes is number => bytes !== null);
  if (read.length === 0) return null;
  return { bytes: read.reduce((sum, bytes) => sum + bytes, 0), partial: read.length < sizes.length };
}

const DECIMAL = ["B", "kB", "MB", "GB", "TB", "PB"];

/**
 * Bytes in Docker's own notation, so a total reads in the units of the rows it
 * sums. Not `formatBytes`: that divides by 1024, and a total in one base beside
 * rows in the other disagrees with them by 7% at gigabytes.
 */
export function formatDockerSize(bytes: number): string {
  let size = bytes;
  let unit = 0;
  while (size >= 1000 && unit < DECIMAL.length - 1) {
    size /= 1000;
    unit += 1;
  }
  return `${Number(size.toPrecision(4))}${DECIMAL[unit]}`;
}
